<script setup>
import { vacanciesFilterDataValues } from '@/constants/vacancies';

const vacanciesStore = useVacanciesStore();
const { t } = useI18n();

const { filterQuery, filterModels } = useQueryFilter(vacanciesFilterDataValues);

const filterOptions = computed(() => {
  const optionAll = {
    name: t('vacancies.groups.all'),
    id: null,
  };

  const options = vacanciesStore.groups.map((groupData) => ({
    name: groupData.name,
    id: groupData.id,
  }));

  return [optionAll, ...options];
});

const page = ref(1);

watch(filterQuery, () => {
  page.value = 1;
});

const limit = 10;
const offset = computed(() => (page.value - 1) * limit);

// vacancies
const requests = [
  useAsyncData(
    () =>
      vacanciesStore.fetchVacancies({
        filter: filterQuery.value,
        limit,
        offset: offset.value,
      }),
    { watch: [filterQuery, page] },
  ),
];

if (!vacanciesStore.groups) {
  requests.push(vacanciesStore.fetchGroups());
}

const [{ data: vacanciesData, pending: vacanciesPending }] =
  await Promise.all(requests);

const pageCount = computed(() =>
  Math.ceil(vacanciesData.value.meta.total / limit),
);

function getLinkAttributes(vacancy) {
  return { to: `/vacancies/${vacancy.id}` };
}

function getGroupTitle(vacancy) {
  return vacanciesStore.getGroupById(vacancy.groupId).name;
}

const rootRef = ref(null);
onMounted(() => {
  watch(vacanciesData, async () => {
    await awaitDoubleRAF();
    safeScrollToElement(rootRef.value.$el);
  });
});
</script>

<template>
  <div class="ctr-vacancy-content">
    <BundlesList
      v-if="vacanciesData"
      ref="rootRef"
      v-model:filter="filterModels.groupId"
      v-model:page="page"
      :title="$t('vacancies.heading')"
      :filter-options="filterOptions"
      :items="vacanciesData.vacancies"
      :items-pending="vacanciesPending"
      :get-link-attributes="getLinkAttributes"
      :page-count="pageCount"
      link-title-key="name"
      :link-group-key="!filterModels.groupId ? 'groupId' : undefined"
      :get-group-title="getGroupTitle"
    />

    <BundlesFoot class="ctr-vacancy-content__foot">
      <template #column-1>
        <ButtonsUnderlined
          :title="$t('vacancies.wantWork')"
          url="/vacancies/form"
          target="_self"
        />
      </template>

      <template #column-2>
        <div
          class="ctr-vacancy-content__wrapper ctr-vacancy-content__wrapper--hh"
        >
          <a
            href="https://hh.ru/employer/1654251"
            target="_blank"
            class="ctr-vacancy-content__link ctr-vacancy-content__link--hh link"
          >
            <img
              class="ctr-vacancy-content__image ctr-vacancy-content__image--hh"
              src="/images/hh.svg"
              alt="hh"
            />

            <span
              class="ctr-vacancy-content__font ctr-vacancy-content__font--hh"
            >
              {{ $t('vacancies.allVacanciesOnHH') }}
            </span>
          </a>
        </div>
      </template>
    </BundlesFoot>
  </div>
</template>

<style scoped lang="scss">
.ctr-vacancy-content {
  &__foot {
    margin-top: em(104);

    @include media-breakpoint-down(sm) {
      margin-top: em(87);
    }
  }

  &__wrapper {
    &--hh {
      display: flex;
    }
  }

  &__link {
    &--hh {
      display: flex;
      align-items: center;
      gap: em(17);
      margin-top: em(-10);

      @include media-breakpoint-down(sm) {
        gap: em(14);
        margin-top: em(-14);
      }
    }
  }

  &__image {
    &--hh {
      @include box(em(53));

      @include media-breakpoint-down(sm) {
        @include box(em(40));
      }
    }
  }

  &__font {
    &--hh {
      font-size: em(32);
      line-height: em(35, 32);

      @include media-breakpoint-down(sm) {
        font-size: em(20);
        line-height: em(22, 20);
      }
    }
  }
}
</style>
